import { forwardRef, memo } from 'react';
import styled from 'styled-components';
import { applySX, type WithSX } from '@elfsight/lib-utils';
import { createReadableClass } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import {
  Buttons,
  CloseButton,
  Heading,
  Timer,
  useElements
} from '../../elements';
import { Deactivation, FreeLink, WidgetBackground } from '../../main';
import { THUMBNAIL_MODE_WIDTH } from '../constants';
import { useLayout } from '../layout-context';
import { useIsBarOverflowing } from '../utils';

const VERTICAL_PADDING = 12;
const VERTICAL_PADDING_WITH_FREE_LINK: [number, number] = [34, 12];
const HORIZONTAL_PADDING = 20;
const HORIZONTAL_PADDING_WITH_CLOSE_BUTTON: [number, number] = [20, 40];

const DESKTOP_GAP = 32;
const MEDIUM_GAP = 20;
const ROW_GAP = 12;

export const LayoutBar = memo(
  forwardRef<HTMLDivElement, unknown>((_, ref) => {
    const { showCloseButton, displayFreeLink, renderThumbnailMode } =
      useSettings();
    const { buttons } = useElements();
    const { isMediumBreakpoint } = useLayout();
    const withMultipleButtons = buttons.length > 1;

    const {
      wrapperRef,
      isOverflowingWrapper,
      containerRef,
      isOverflowingContainer
    } = useIsBarOverflowing();

    const verticalPadding = displayFreeLink
      ? VERTICAL_PADDING_WITH_FREE_LINK
      : VERTICAL_PADDING;
    const horizontalPadding = showCloseButton
      ? HORIZONTAL_PADDING_WITH_CLOSE_BUTTON
      : HORIZONTAL_PADDING;
    const columnGap = isMediumBreakpoint ? MEDIUM_GAP : DESKTOP_GAP;

    const isInRow = !isOverflowingContainer && !isOverflowingWrapper;
    const onlyButtonsWrapped =
      withMultipleButtons && isOverflowingContainer && !isOverflowingWrapper;

    return (
      <Deactivation childrenRef={ref}>
        <WidgetBackground
          style={
            renderThumbnailMode
              ? {
                  maxWidth: THUMBNAIL_MODE_WIDTH,
                  margin: '0 auto'
                }
              : {}
          }
          verticalPadding={verticalPadding}
          horizontalPadding={horizontalPadding}
        >
          <Container
            ref={containerRef}
            $sx={{
              columnGap,
              alignItems: !isOverflowingContainer ? 'center' : 'start',
              flexDirection: isOverflowingContainer ? 'column' : 'row'
            }}
          >
            <ContentWrapper
              ref={wrapperRef}
              $sx={{
                columnGap,
                alignItems: isInRow || onlyButtonsWrapped ? 'center' : 'start',
                flexDirection: isOverflowingWrapper ? 'column' : 'row'
              }}
            >
              <Heading />

              <Timer />
            </ContentWrapper>

            <Buttons
              horizontalPadding={horizontalPadding}
              withScroll={isOverflowingContainer}
            />

            <CloseButton />

            <FreeLink />
          </Container>
        </WidgetBackground>
      </Deactivation>
    );
  })
);

const Container = styled.div.attrs({
  className: createReadableClass({
    component: 'layout-bar',
    element: 'container'
  })
})<WithSX>`
  ${applySX};
  justify-content: center;
  display: flex;
  text-align: left;
  row-gap: ${ROW_GAP}px;
`;

const ContentWrapper = styled.div.attrs<WithSX>({
  className: createReadableClass({
    component: 'layout-bar',
    element: 'content-wrapper'
  })
})`
  ${applySX};
  max-width: 100%;
  display: flex;
  justify-content: inherit;
  row-gap: ${ROW_GAP}px;
`;
